// components/layout/Sidebar.jsx
import {
  FaLayerGroup,
  FaClipboardCheck,
  FaListUl,
  FaClipboardList,
  FaClock,
  FaBoxes,
  FaUsersCog,
  FaCog,
} from "react-icons/fa";
import { IoNotifications } from "react-icons/io5";
import { MdDashboard } from "react-icons/md";
import UserProfile from "./UserProfile";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutService } from "../../../Services/authService";
import { clearCreateMaterialList } from "../../../Redux/Slice/materialListSlice";
import { clearProject } from "../../../Redux/Slice/projectSlice";
import { ClearMaterialListWithProject } from "../../../Redux/Slice/materialListWithProjectSlice";
import { clearSubscription } from "../../../Redux/Slice/subscriptionSlice";
import { useDispatch, useSelector } from "react-redux";
import { t } from "../../../utils/translate";
import { store } from "../../../Redux/store";
import { TrendingUp, Warehouse } from "lucide-react";

const ADMIN_ROLE = 4;

// Navigation configuration
const createNavItems = (userRole) => [
  {
    section: "MAIN",
    items: [
      { name: "Dashboard", icon: MdDashboard, path: "/new-admin-dashboard" },
      {
        name: "Notification",
        icon: IoNotifications,
        path: "/admin/notification",
      },
    ],
  },
  {
    section: "SCAFFOLDING",
    items: [
      { name: "Projects", icon: FaLayerGroup, path: "/admin/projects" },
      {
        name: "Approvals",
        icon: FaClipboardList,
        path: "/admin/approval-form",
      },
      {
        name: "After Control",
        icon: FaClipboardCheck,
        path: "/admin/after-control",
      },
      { name: "Scaffold Log", icon: FaListUl, path: "/admin/project-log-page" },
      { name: "Observations", icon: FaLayerGroup, path: "/admin/observations" },
      { name: "SJA", icon: FaClipboardList, path: "/admin/sja" },
      {
        name: "Material List",
        icon: FaClipboardList,
        path: "/admin/material-list",
      },
    ],
  },
  {
    section: "MANAGEMENT",
    items: [
      { name: "Time Tracking", icon: FaClock, path: "/admin/time-tracking" },
      { name: "Work Hours", icon: FaBoxes, path: "/admin/time-track-list" },
      {
        name: "Admin Management",
        icon: FaBoxes,
        path: "/admin/admin-management",
      },
      { name: "Inventory", icon: Warehouse, path: "/admin/inventory" },
      // {
      //   name: "Transfer Materials",
      //   icon: TrendingUp,
      //   path: "/admin/transfer-material",
      // },
    ],
  },
  {
    section: "ADMINISTRATION",
    items: [
      { name: "Users & Access", icon: FaUsersCog, path: "/admin/users-access" },
      ...(userRole === ADMIN_ROLE
        ? [
            { name: "Subscription", icon: FaCog, path: "/admin/subscription" },
            {
              name: "My Subscription",
              icon: FaClipboardList,
              path: "/admin/admin-subscription",
            },
          ]
        : []),
    ],
  },
];

const clearAllReduxData = () => {
  store.dispatch(clearCreateMaterialList());
  store.dispatch(clearProject());
  store.dispatch(ClearMaterialListWithProject());
  store.dispatch(clearSubscription());
};

export default function Sidebar({ sidebarOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = useSelector((state) => state?.auth?.loggedInUser?.type);

  const isActive = (path) => location.pathname === path;

  const navItems = createNavItems(userRole);

  const handleLogout = async () => {
    try {
      localStorage.removeItem("hasSeenModal");
      await logoutService();
      clearAllReduxData();
      toast.success(t("logoutSuccessfully"));
      navigate("/signin");
    } catch (error) {
      toast.error("Logout failed. Please try again.");
    }
  };

  const sidebarClasses = `
    fixed md:static inset-y-0 left-0 z-50 transform
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
    md:translate-x-0 transition-transform duration-300 ease-in-out
    bg-white border-r w-64 flex flex-col
  `.trim();

  const getLinkClasses = (path) =>
    `
    flex items-center p-2 rounded transition-colors
    ${
      isActive(path)
        ? "bg-blue-50 text-blue-600"
        : "text-gray-700 hover:bg-gray-100"
    }
  `.trim();

  return (
    <div className={sidebarClasses}>
      {/* Logo */}
      <div className="flex items-center justify-center">
        <img
          src="/new_logo.svg"
          alt="Company Logo"
          className="w-[150px] h-[60px]"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto">
        {navItems.map((section) => (
          <div key={section.section} className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 mb-2">
              {section.section}
            </h3>
            <ul className="space-y-2">
              {section.items.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={getLinkClasses(item.path)}
                    aria-label={`Navigate to ${item.name}`}
                  >
                    <item.icon className="mr-3" size={20} aria-hidden="true" />
                    <span>{item.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="border border-solid text-black p-4 bg-gray-50 hover:bg-gray-100 
                   transition-colors text-md font-semibold w-full text-left"
        aria-label="Logout from application"
      >
        Log out
      </button>

      {/* User Profile */}
      <UserProfile />
    </div>
  );
}
